/**
 * "Reset explore usage" — the admin trigger for watchlock's `resetExploreUsage`
 * callable (PR #1029), shown on the user detail modal.
 *
 * Self-contained (button, its own confirmation, its own in-flight state) because
 * UserManagement.tsx is already 3,000 lines of one component and because the
 * behaviour worth testing here — the confirm gate, the receipt, the failure
 * path — is untestable once it is tangled into that file's Firestore wiring.
 *
 * The success toast quotes the new `resetVersion` when the callable reports one.
 * That number is the receipt: the RN client compares it against its cached copy
 * on the next launch, so seeing it move is how an admin knows the write landed.
 * When no version comes back the toast says so rather than printing a number we
 * did not read — an unverifiable success is still a success, but it should not
 * dress itself up as a verified one.
 */

import { useState } from 'react';
import toast from 'react-hot-toast';

import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { resetExploreUsage } from '../../services/exploreUsageReset';

interface ResetExploreUsageButtonProps {
  uid: string;
  email?: string | null;
}

export function ResetExploreUsageButton({
  uid,
  email,
}: ResetExploreUsageButtonProps) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  // Never an empty name in the sentence: a user with no email is identified by
  // the uid, which is the only thing that is always present.
  const who = email?.trim() ? email.trim() : uid;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const { resetVersion } = await resetExploreUsage(uid);
      toast.success(
        resetVersion === null
          ? `Explore usage reset for ${who} (no resetVersion reported)`
          : `Explore usage reset for ${who} — resetVersion ${resetVersion}`,
      );
      setConfirming(false);
    } catch (error) {
      // The dialog stays open on failure so the action can be retried without
      // hunting for the button again.
      const message =
        error instanceof Error ? error.message : 'unknown error';
      toast.error(`Failed to reset explore usage: ${message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Explore usage
      </label>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setConfirming(true)}
      >
        Reset explore usage
      </Button>
      <p className="text-xs text-gray-500 mt-2">
        Clears this user's free-tier explore counter. Their device picks the
        reset up on its next launch or sync.
      </p>

      <Modal
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        title="Reset Explore Usage"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Reset explore counter for{' '}
            <span className="font-medium text-gray-900">{who}</span>? This bumps
            their resetVersion and their device will clear its local cache on
            next launch or sync.
          </p>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm font-medium text-gray-900">
              {email?.trim() || 'No email'}
            </p>
            <p className="text-xs text-gray-500 font-mono mt-1">{uid}</p>
          </div>
          {/* The wait is real and it is long: this function cold-starts in
              roughly a minute and a half, and a spinner that long with no
              explanation reads as a broken button rather than a slow one. */}
          {busy && (
            <p className="text-xs text-gray-500">
              Waiting for the server. The first reset after an idle spell can
              take up to two minutes while the function starts — leave this
              open.
            </p>
          )}
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setConfirming(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm} loading={busy} disabled={busy}>
              Reset explore usage
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
